'use client'

import { useEffect, useId, useState } from "react"
import { getStaffByRole, getName } from "@/services/aroeven"
import { parsePhone } from "@/services/text"
import { createJobApplication } from "@/api/actions"
import { status as s } from '@/services/status'
import Loading from "app/loading"
import { z } from 'zod'
import { messages } from '@/services/validation'
import { executeRecaptcha, useRecaptcha } from "@/services/recaptcha/client"

type ApplicationSidebarProps = {
    aroeven: Aroeven,
    job: Job,
    proposedBy: Aroeven & AroevenStaffs
}

const ApplicationSchema = z.object({
    name: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    email: z.string().trim().min(1, messages.required).email(messages.email),
    phone: z.string().trim().min(1, messages.required).min(10, messages.tooShort).max(255, messages.tooLong),
    resume: z.object({}, {
        message: messages.required
    }),
    coverLetter: z.object({}, {
        message: messages.required
    }),
    longTermRetention: z.boolean()
})

export default function ApplicationSidebar({
    aroeven,
    job,
    proposedBy
}: ApplicationSidebarProps) {

    const [application, setApplication] = useState({
        name: '',
        email: '',
        phone: '',
        resume: null,
        coverLetter: null,
        longTermRetention: false
    })
    useRecaptcha()

    const [errors, setErrors] = useState([])
    const [status, setStatus] = useState(s.idle)

    const longTermRetentionCheckboxId = useId()

    async function postApplication() {
        setErrors([])
        try {
            ApplicationSchema.parse(application)
        }
        catch (error) {
            setErrors(error.issues)
            return
        }

        setStatus(s.loading)
        try {
            const token = await executeRecaptcha('job_application')

            const formData = new FormData()
            formData.append('coverLetter', application.coverLetter)
            formData.append('resume', application.resume)
            formData.append('name', application.name)
            formData.append('email', application.email)
            formData.append('phone', application.phone)
            formData.append('longTermRetention', application.longTermRetention ? '1' : '0')

            const sentApplication = await createJobApplication(aroeven, job.id, formData, token)
            if (!sentApplication) {
                throw new Error('Application not sent')
            }

            setStatus(s.finished)
        }
        catch (error) {
            console.log(error)
            setStatus(s.error)
        }
    }

    return (
        <>
            {status === s.loading &&
                <Loading />
            }
            <div className="d-flex flex-column flex-md-row flex-lg-column justify-end" data-offset={450}>
                <div className="encart-2 ml-lg-0 px-30 py-30 bg-bloc w-360">
                    <div className="row y-gap-15 items-center justify-between mb-2">
                        <div className="col-auto">
                            <div className="text-14 text-light-1">
                                <h4>Référence : {job.reference}</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row y-gap-15 items-center justify-between">
                        <div className="col-auto">
                            <div className="text-14 text-light-1">
                                <h4>Proposé par :</h4>
                                <h4>
                                    <a href='#' className="link">
                                        {getName(job.proposedBy)} <i className="fi fi-ss-angle-circle-right lh-15" />
                                    </a>
                                </h4>
                            </div>
                        </div>
                        <div className="col-auto text-ocre-dark numbers text-18">
                            <i className="fi fi-rr-phone-flip" /> Contactez {getStaffByRole(proposedBy, 'job')?.firstName ?? 'l\'Aroéven'} au {parsePhone(getStaffByRole(proposedBy, 'job')?.phone ?? proposedBy.phone)}
                        </div>
                    </div>
                    <div className="separateur d-flex flex-row justify-content-between">
                        <div className="divider" />
                        <div>ou</div>
                        <div className="divider" />
                    </div>
                    {status === s.finished &&
                        <>
                            <div className="alert alert-success" role="alert">
                                <h4 className="alert-heading">Bien joué !</h4>
                                <p>Votre candidature a bien été envoyée.</p>
                            </div>
                        </>
                    }
                    {[s.idle, s.error].includes(status) &&
                        <>
                            <div className="row y-gap-20">
                                <div className="col-auto text-primary-4 numbers text-18">Complétez la candidature en ligne :</div>
                                <div className="form-input">
                                    <input type="text" className={errors.map(e => e.path[0]).indexOf('name') !== -1 ? 'requis' : ''} value={application.name} onChange={(e) => setApplication({
                                        ...application,
                                        name: e.target.value
                                    })} />
                                    <label className="lh-1 text-16 text-light-1">
                                        Nom et prénom <span className="obligatoire">*</span>
                                    </label>
                                </div>
                                {errors.map(e => e.path[0]).indexOf('name') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'name')[0].message}</div>}
                                <div className="form-input">
                                    <input type="text" className={errors.map(e => e.path[0]).indexOf('email') !== -1 ? 'requis' : ''} value={application.email} onChange={(e) => setApplication({
                                        ...application,
                                        email: e.target.value
                                    })} />
                                    <label className="lh-1 text-16 text-light-1">
                                        Adresse email <span className="obligatoire">*</span>
                                    </label>
                                </div>
                                {errors.map(e => e.path[0]).indexOf('email') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'email')[0].message}</div>}
                                <div className="form-input">
                                    <input type="text" className={errors.map(e => e.path[0]).indexOf('phone') !== -1 ? 'requis' : ''} value={application.phone} onChange={(e) => setApplication({
                                        ...application,
                                        phone: e.target.value
                                    })} />
                                    <label className="lh-1 text-16 text-light-1">
                                        Téléphone <span className="obligatoire">*</span>
                                    </label>
                                </div>
                                {errors.map(e => e.path[0]).indexOf('phone') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'phone')[0].message}</div>}
                                <label htmlFor="formFile" className="form-label m-0 p-0 ml-20 text-primary-4 numbers text-18">
                                    CV <span className="obligatoire">*</span>
                                </label>
                                <div className="form-input pt-0">
                                    <input className={"form-control" + (errors.map(e => e.path[0]).indexOf('resume') !== -1 ? ' requis' : '')} type="file" accept={'application/pdf, application/msword, application/vnd.apple.pages, application/vnd.oasis.opendocument.text'} onChange={(e) => {
                                        if (e.target.files.length === 0) return
                                        setApplication({
                                            ...application,
                                            resume: e.target.files[0]
                                        })
                                    }} />
                                </div>
                                {errors.map(e => e.path[0]).indexOf('resume') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'resume')[0].message}</div>}
                                <label htmlFor="formFile" className="form-label m-0 p-0 ml-20 text-primary-4 numbers text-18">
                                    Lettre de motivation <span className="obligatoire">*</span>
                                </label>
                                <div className="form-input pt-0">
                                    <input className={"form-control" + (errors.map(e => e.path[0]).indexOf('coverLetter') !== -1 ? ' requis' : '')} type="file" accept={'application/pdf, application/msword, application/vnd.apple.pages, application/vnd.oasis.opendocument.text'} onChange={(e) => {
                                        if (e.target.files.length === 0) return
                                        setApplication({
                                            ...application,
                                            coverLetter: e.target.files[0]
                                        })
                                    }} />
                                </div>
                                {errors.map(e => e.path[0]).indexOf('coverLetter') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'coverLetter')[0].message}</div>}
                                <div className="col-12">
                                    <div className="checkbox-wrapper">
                                        <input type="checkbox" id={longTermRetentionCheckboxId} checked={application.longTermRetention} onChange={() => setApplication({
                                            ...application,
                                            longTermRetention: !application.longTermRetention
                                        })} />
                                        <label htmlFor={longTermRetentionCheckboxId}>
                                            <span />
                                            Je souhaite que l'Aroéven conserve mon dossier de candidature pour d'autres offres
                                        </label>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="col-12 mt-5">
                                        <button className="button-primary demande d-block w-100 mb-10" onClick={(e) => {
                                            e.preventDefault()
                                            postApplication()
                                        }}>Envoyer</button>
                                    </div>
                                </div>
                            </div>
                        </>
                    }
                </div>
            </div>
        </>
    )
}